import crypto from 'crypto';

// Encryption configuration
const ALGORITHM = 'aes-256-gcm';
const IV_LENGTH = 16; // For AES, this is always 16 bytes

/**
 * @description Encrypt a string using AES-256-GCM (server-side only)
 * @param text The text to encrypt
 * @param encryptionKey Optional custom encryption key. If not provided, uses AUTH_API_KEY_ENCRYPTION_SECRET from environment
 * @returns The encrypted text as a base64 string with IV and auth tag
 * @example
 * // Using environment variable key
 * const encrypted = encryptServerSide('sensitive data');
 *
 * // Using custom key
 * const encrypted = encryptServerSide('sensitive data', 'my-custom-secret-key');
 */
export const encryptServerSide = (text: string, encryptionKey?: string): string => {
  try {
    // Use provided key or fall back to environment variable
    // This should be a 32-byte (256-bit) key for AES-256
    const secretKey = encryptionKey || process.env.AUTH_API_KEY_ENCRYPTION_SECRET;

    if (!secretKey) {
      console.error('No encryption key provided and AUTH_API_KEY_ENCRYPTION_SECRET environment variable is not set');
      return text; // Return original text if no key is available
    }

    // Create a buffer from the key (must be 32 bytes for AES-256)
    const key = crypto.createHash('sha256').update(secretKey).digest();

    // Generate a random initialization vector
    const iv = crypto.randomBytes(IV_LENGTH);

    // Create cipher
    const cipher = crypto.createCipheriv(ALGORITHM, key, iv);

    // Encrypt the text
    let encrypted = cipher.update(text, 'utf8', 'base64');
    encrypted += cipher.final('base64');

    // Get the authentication tag
    const authTag = cipher.getAuthTag();

    // Combine IV, encrypted text, and auth tag
    // Format: base64(iv):base64(encrypted):base64(authTag)
    return `${iv.toString('base64')}:${encrypted}:${authTag.toString('base64')}`;
  } catch (error) {
    console.error('Encryption error:', error);
    // Return the original text if encryption fails
    return text;
  }
};

/**
 * @description Decrypt a string that was encrypted with AES-256-GCM (server-side only)
 * @param encryptedText The encrypted text (format: base64(iv):base64(encrypted):base64(authTag))
 * @param encryptionKey Optional custom encryption key. If not provided, uses AUTH_API_KEY_ENCRYPTION_SECRET from environment
 * @returns The decrypted text
 * @example
 * // Using environment variable key
 * const decrypted = decryptServerSide(encryptedText);
 *
 * // Using custom key
 * const decrypted = decryptServerSide(encryptedText, 'my-custom-secret-key');
 */
export const decryptServerSide = (encryptedText: string, encryptionKey?: string): string => {
  try {
    // Check if the text is in the expected format
    const parts = encryptedText.split(':');
    if (parts.length !== 3) {
      // Not in the expected format, return as is
      return encryptedText;
    }

    // Use provided key or fall back to environment variable
    const secretKey = encryptionKey || process.env.AUTH_API_KEY_ENCRYPTION_SECRET;

    if (!secretKey) {
      console.error('No encryption key provided and AUTH_API_KEY_ENCRYPTION_SECRET environment variable is not set');
      return encryptedText; // Return encrypted text if no key is available
    }

    // Create a buffer from the key (must be 32 bytes for AES-256)
    const key = crypto.createHash('sha256').update(secretKey).digest();

    // Extract IV, encrypted text, and auth tag
    const iv = Buffer.from(parts[0], 'base64');
    const encrypted = parts[1];
    const authTag = Buffer.from(parts[2], 'base64');

    // Create decipher
    const decipher = crypto.createDecipheriv(ALGORITHM, key, iv);
    decipher.setAuthTag(authTag);

    // Decrypt the text
    let decrypted = decipher.update(encrypted, 'base64', 'utf8');
    decrypted += decipher.final('utf8');

    return decrypted;
  } catch (error) {
    console.error('Decryption error:', error);
    // Return the encrypted text if decryption fails
    return encryptedText;
  }
};
